import { listCourses, fetchBatchCourseUnitStatus } from 'api/coachDashboard';
import { ApiResponse } from 'apisauce';
import { cast, flow, getEnv, IAnyModelType, Instance, types } from 'mobx-state-tree';
import { PageStore } from '../Page';
import { format, parse } from 'fecha';

export function CourseLate(): IAnyModelType {
  return types.model('Course', {
    name: types.string,
    title: types.string,
    subject: types.union(
      types.literal('coding'),
      types.literal('science'),
      types.literal('core'),
      types.literal('holistic'),
      types.literal('neoschool'),
      types.literal('eveningschool')
    ),
    loading: false,
    loaded: false,
  });
}

export interface ICourse extends Instance<ReturnType<typeof CourseLate>> { }

// Model for individual course units
export const CourseUnit = types.model('CourseUnit', {
  name: types.string,
  title: types.string,
});

export interface ICourseUnit extends Instance<typeof CourseUnit> { }

// Model for unit enrollment dates
export const BatchUnitStatus = types.model('BatchUnitStatus', {
  started_at: types.string,
  ended_at: types.string,
}).views((self) => ({
  get startAtDate() {
    return parse(self.started_at, 'YYYY-MM-DD');
  },
  get endAtDate() {
    return parse(self.ended_at, 'YYYY-MM-DD');
  },
  get startAtFormatted() {
    const date = parse(self.started_at, 'YYYY-MM-DD');
    return format(date!, 'MMM D, YYYY');
  },
  get endAtFormatted() {
    const date = parse(self.ended_at, 'YYYY-MM-DD');
    return format(date!, 'MMM D, YYYY');
  }
}));

export interface IBatchUnitStatus extends Instance<typeof BatchUnitStatus> { }

// Model for the batch course unit status response
export const BatchCourseUnitStatusResponse = types.model('BatchCourseUnitStatusResponse', {
  units: types.array(CourseUnit),
  batches: types.map(types.map(BatchUnitStatus)), // Map<batchId, Map<unitName, BatchUnitStatus>>
});

export interface IBatchCourseUnitStatusResponse extends Instance<typeof BatchCourseUnitStatusResponse> { }

export const CourseStore = types
  .model('CourseStore', {
    courses: types.array(types.late(CourseLate)),
    loading: false,
    loaded: false,
    page: types.maybeNull(PageStore),
    batchCourseUnitStatusMap: types.map(BatchCourseUnitStatusResponse),
    batchCourseUnitStatusLoading: false,
    batchCourseUnitStatusLoaded: false,
  })
  .actions((self) => ({
    listCourses: flow(function* ({ subject }: { subject?: string }) {
      try {
        self.loading = true;
        self.loaded = false;
        const response: ApiResponse<any> = yield listCourses({ subject });
        if (response.problem) {
          getEnv(self).commonStore.setNetworkProblem(response.problem);
          return;
        }
        self.courses = cast(response.data);
        self.loaded = true;
      } finally {
        self.loading = false;
      }
    }),
    clearCourses: () => {
      self.courses = cast([]);
      self.loaded = false;
    },
    fetchBatchCourseUnitStatus: flow(function* ({ courseName, showEndedBatches }: { courseName: string, showEndedBatches?: boolean }) {
      try {
        self.batchCourseUnitStatusLoading = true;
        const response: ApiResponse<any> = yield fetchBatchCourseUnitStatus({ courseName, showEndedBatches });
        if (response.problem) {
          getEnv(self).commonStore.setNetworkProblem(response.problem);
          return;
        }
        self.batchCourseUnitStatusMap.set(courseName, cast(response.data));
        self.batchCourseUnitStatusLoaded = true;
      } finally {
        self.batchCourseUnitStatusLoading = false;
      }
    }),
    clearBatchCourseUnitStatus: () => {
      self.batchCourseUnitStatusMap.clear();
      self.batchCourseUnitStatusLoaded = false;
    },
  }));

export interface ICourseStore extends Instance<typeof CourseStore> { }
